import i18next from 'i18next';
import moment from 'moment';
import * as React from 'react';
import 'moment/dist/locale/fi';
import 'moment/dist/locale/en-gb';

// Force Monday as the first day of the week for moment's base 'en' locale
// (MUI DatePickers read this via AdapterMoment when adapterLocale='en')
moment.updateLocale('en', { week: { dow: 1, doy: 4 } });

// Typed reference to i18next.t — workaround for i18next v25 types requiring TS 5.0+
const t = i18next.t.bind(i18next) as unknown as
  (key: string | string[], options?: Record<string, unknown>) => string;

// Initialize i18next (resources are registered via translateService.registerTranslations)
i18next.init({
  lng: 'fi',
  fallbackLng: 'en',
  showSupportNotice: false,
  interpolation: {
    escapeValue: false, // React already escapes
    prefix: '%(',
    suffix: ')s',
  },
  resources: {},
});

// Translate component — drop-in replacement for react-translate-component
export const Translate: React.FC<any> = (props: any) => {
  const { content, children, ...interpolationProps } = props;
  const key = content || children;
  if (!key) return null;

  // The 'with' prop from react-translate-component maps to interpolation values
  const interpolation = props.with || {};
  const translated = t(key, interpolation);
  return <>{translated}</>;
};

export class translateService {
  static translate(key: string, lang?: string): string {
    if (lang) {
      return t(key, { lng: lang });
    }
    const result = t(key);
    // Fallback behavior: if translation is missing, try English with prefix
    if (result === key && i18next.language !== 'en') {
      const enResult = t(key, { lng: 'en' });
      if (enResult !== key) {
        return '(EN) ' + enResult;
      }
    }
    return result as string;
  }

  static localize(date: string): string {
    return moment(date).format('l LT');
  }

  static localizeRange(startDate: string, endDate: string): string {
    return moment(startDate).format('l LT') + ' - ' + moment(endDate).format('l');
  }

  static getWeekDay(date: string): string {
    return moment(date, 'DD.MM.YYYY').clone().format("dddd");
  }

  static fromNow(startDate: string): string {
    return moment(startDate).fromNow();
  }

  static localizeDuration(startDate: string, endDate: string): string {
    return moment.duration(moment(endDate).diff(moment(startDate))).humanize();
  }

  static setLocale(lang: string): void {
    i18next.changeLanguage(lang);
    moment.locale(lang);
  }

  static registerTranslations(lang: string, langObject: any): void {
    i18next.addResourceBundle(lang, 'translation', langObject, true, true);
  }

  /** Whether a language's translations have been registered (English is loaded on demand, row I2). */
  static hasTranslations(lang: string): boolean {
    return i18next.hasResourceBundle(lang, 'translation');
  }
}
